/* @ngInject */

export default (PaymentFrequency) => {
  /*
   * Constructor
   * --------------------------------------------------------------------------------
   */
  function AssetFinanceCompareProduct(
    id,
    productDetails,
    selectedProduct,
    repayments,
    estimate,
    costs,
    comparativeSaving,
    commissions,
    commissionsPayable,
    sourceOfBusinessNumber,
    isBestSaving,
    isPreferred
  ) {
    this.id = id;
    this.productDetails = productDetails;
    this.selectedProduct = selectedProduct;
    this.repayments = repayments;
    this.estimate = estimate;
    this.costs = costs;
    this.comparativeSaving = comparativeSaving;
    this.commissions = commissions;
    this.commissionsPayable = commissionsPayable;
    this.sourceOfBusinessNumber = sourceOfBusinessNumber;
    this.isBestSaving = isBestSaving;
    this.isPreferred = isPreferred;

    _.each(this.productDetails, (val, key, obj) => {
      if (key === 'paymentFrequency' && typeof val === 'number') {
        switch (val) {
          case 0:
            obj[key] = PaymentFrequency.none;
            break;
          case 52:
            obj[key] = PaymentFrequency.weekly;
            break;
          case 26:
            obj[key] = PaymentFrequency.fortnightly;
            break;
          case 12:
            obj[key] = PaymentFrequency.monthly;
            break;
          case 4:
            obj[key] = PaymentFrequency.quarterly;
            break;
          case 2:
            obj[key] = PaymentFrequency.semiannual;
            break;
          case 1:
            obj[key] = PaymentFrequency.annually;
            break;
          default:
            break;
        }
      }
      if (key === 'paymentFrequency' && typeof val !== 'number') {
        obj[key] = PaymentFrequency.of(val?.name);
      }
    });

    if (!this.commissionsPayable?.upfront) {
      this.commissionsPayable.upfront = 0;
    }
    if (!this.commissionsPayable?.trail) {
      this.commissionsPayable.trail = 0;
    }
    if (!this.productDetails?.processingFee) {
      this.productDetails.processingFee = 0;
    }
    if (!this.productDetails?.brokerFee) {
      this.productDetails.brokerFee = 0;
    }
  }

  /*
   * Static methods
   * --------------------------------------------------------------------------------
   */
  AssetFinanceCompareProduct.Blank = function () {
    return AssetFinanceCompareProduct.Build({
      id: _.uuid(),
      productDetails: {
        loanAmount: 0,
        grossCost: 0,
        amountExGstVat: 0,
        netCost: 0,
        gstVatAmount: 0,
        netAmountFinanced: 0,
        isNetAmountFinancedEdited: false,
        netAmountFinancedEdited: 0,
        term: 0,
        interestRate: 0,
        isInterestRateEdited: false,
        brokerCommissionValue: 0,
        brokerCommissionPercent: 0,
        rateDiscount: 0,
        isRateDiscountEdited: false,
        brokerOriginationFee: 0,
        isBrokerOriginationFeeCapitalized: false,
        processingFee: 0,
        brokerFee: 0,
        upfrontFee: 0,
        isUpfrontFeeEdited: false,
        isUpfrontFeeCapitalised: false,
        payments: false,
        paymentFrequency: PaymentFrequency.monthly,
        estimatedBorrowerRate: 0,
        productType: 0,
        balloonValue: 0,
        balloonPercent: 0,
        lvr: 0,
        gstOfEstimatedRepayment: 0,
        estimatedRepaymentWithGst: 0,
        loanType: '',
        totalBrokerBrokerage: 0,
        totalAggregatorBrokerage: 0,
        totalBrokerage: 0,
        aggregatorCommissionValue: 0
      },
      selectedProduct: null,
      repayments: {
        repayment: 0,
        repaymentWithBuffer: 0,
        monthlyFee: 0
      },
      estimate: {
        netMonthlySurplus: 0
      },
      costs: {
        principal: 0,
        interest: 0,
        fees: 0,
        total: 0
      },
      comparativeSaving: 0,
      commissions: {
        loanAccountNumber: '',
        isVbi: false,
        redbook: false
      },
      commissionsPayable: {
        upfront: 0,
        trail: 0
      },
      sourceOfBusinessNumber: '',
      isBestSaving: false,
      isPreferred: false
    });
  };

  AssetFinanceCompareProduct.Build = function (data) {
    return new AssetFinanceCompareProduct(
      data.id,
      data.productDetails
        ? data.productDetails
        : {
            loanAmount: 0,
            grossCost: 0,
            amountExGstVat: 0,
            netCost: 0,
            gstVatAmount: 0,
            netAmountFinanced: 0,
            isNetAmountFinancedEdited: false,
            netAmountFinancedEdited: 0,
            term: 0,
            interestRate: 0,
            isInterestRateEdited: false,
            brokerCommissionValue: 0,
            brokerCommissionPercent: 0,
            rateDiscount: 0,
            isRateDiscountEdited: false,
            brokerOriginationFee: 0,
            isBrokerOriginationFeeCapitalized: false,
            processingFee: 0,
            brokerFee: 0,
            upfrontFee: 0,
            isUpfrontFeeEdited: false,
            isUpfrontFeeCapitalised: false,
            payments: false,
            paymentFrequency: PaymentFrequency.monthly,
            estimatedBorrowerRate: 0,
            productType: 0,
            balloonValue: 0,
            balloonPercent: 0,
            lvr: 0,
            gstOfEstimatedRepayment: 0,
            estimatedRepaymentWithGst: 0,
            loanType: '',
            totalBrokerBrokerage: 0,
            totalAggregatorBrokerage: 0,
            totalBrokerage: 0,
            aggregatorCommissionValue: 0
          },
      data.selectedProduct || null,
      data.repayments
        ? data.repayments
        : {
            repayment: 0,
            repaymentWithBuffer: 0,
            monthlyFee: 0
          },
      data.estimate
        ? data.estimate
        : {
            estimate: {
              netMonthlySurplus: 0
            }
          },
      data.costs
        ? data.costs
        : {
            principal: 0,
            interest: 0,
            fees: 0,
            total: 0
          },
      data.comparativeSaving ? data.comparativeSaving : 0,
      data.commissions
        ? data.commissions
        : {
            loanAccountNumber: '',
            isVbi: false,
            redbook: false
          },
      data.commissionsPayable
        ? data.commissionsPayable
        : {
            upfront: 0,
            trail: 0
          },
      data.sourceOfBusinessNumber || undefined,
      data.isBestSaving || false,
      data.isPreferred || false
    );
  };

  return AssetFinanceCompareProduct;
};
